import React, { useState } from 'react';
import { ArrowDownLeftIcon, ArrowUpRightIcon, LinkIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { naira } from '../../utils/format';
import { ACCOUNT_LABEL, KIND_LABEL, isBalanced } from '../../utils/ledger';
import type { LedgerTransaction, TransactionState } from '../../types/finance';
const STATE_TONE: Record<TransactionState, 'ok' | 'brand' | 'warn' | 'danger' | 'neutral'> = {
  SETTLED: 'ok',
  RESERVED: 'brand',
  PENDING: 'warn',
  FAILED: 'danger',
  REVERSED: 'neutral',
  REFUNDED: 'neutral'
};
function stateLabel(state: TransactionState): string {
  return state.charAt(0) + state.slice(1).toLowerCase();
}
export function LedgerList({
  transactions
}: {
  transactions: LedgerTransaction[];
}) {
  const [openId, setOpenId] = useState<string>('');
  return <ul className="divide-y divide-line" data-id="element-3222" data-mp="2fefyxeykxs05">
      {transactions.map(transaction => {
      const open = transaction.id === openId;
      const userEntry = transaction.entries.find(entry => entry.account.startsWith('user:'));
      const outgoing = userEntry?.direction === 'debit';
      return <li key={transaction.id} data-id="element-3223" data-mp="29s7a85q2t5zs">
            <button type="button" onClick={() => setOpenId(open ? '' : transaction.id)} aria-expanded={open} className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-3224" data-mp="1vsy3dwnrfo8h">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${outgoing ? 'bg-canvas text-ink-muted' : 'bg-ok-50 text-ok-700'}`} data-id="element-3225" data-mp="1qtmn0orb2krf">
                {outgoing ? <ArrowUpRightIcon className="h-4 w-4" data-id="element-3226" data-mp="1hzglshemwll3" /> : <ArrowDownLeftIcon className="h-4 w-4" data-id="element-3227" data-mp="134tuz3dt0x23" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-3228" data-mp="182vf7rmyv2f5">
                <span className="block text-[13px] font-medium text-ink" data-id="element-3229" data-mp="oyi60jc5jy9y">
                  {transaction.narrative}
                </span>
                <span className="cz-num mt-0.5 block text-[12px] text-ink-muted" data-id="element-3230" data-mp="k79w7m6wuaw9">
                  {transaction.reference} · {KIND_LABEL[transaction.kind]} · {transaction.domain} ·{' '}
                  {transaction.at}
                </span>
              </span>
              <span className="cz-num shrink-0 text-[13px] font-semibold text-ink" data-id="element-3231" data-mp="17xcnw16wxeuc">
                {outgoing ? '−' : '+'}
                {naira(transaction.entries[0].amountNaira)}
              </span>
              <Badge tone={STATE_TONE[transaction.state]} data-id="element-3232" data-mp="xyx3si6az5uc">{stateLabel(transaction.state)}</Badge>
            </button>

            {open && <div className="border-t border-line bg-canvas px-5 py-4" data-id="element-3233" data-mp="sztwer2uonj7">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3234" data-mp="3iqqjf49lzaj3">
                  Double-entry record
                </p>
                <table className="mt-2 w-full text-left" data-id="element-3235" data-mp="3dsxc27wkkah8">
                  <thead data-id="element-3236" data-mp="1t9v35l45rgx6">
                    <tr className="text-[12px] uppercase tracking-wide text-ink-subtle" data-id="element-3237" data-mp="1t6gzm9jrd1o5">
                      <th scope="col" className="py-1 font-semibold" data-id="element-3238" data-mp="1y7wlu4xo9e84">
                        Account
                      </th>
                      <th scope="col" className="py-1 text-right font-semibold" data-id="element-3239" data-mp="27xg5hct7lc41">
                        Debit
                      </th>
                      <th scope="col" className="py-1 text-right font-semibold" data-id="element-3240" data-mp="2cvhnduewf5dx">
                        Credit
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line" data-id="element-3241" data-mp="2lwxwzzxw33h3">
                    {transaction.entries.map(entry => <tr key={`${entry.account}-${entry.direction}`} data-id="element-3242" data-mp="2quzh8o71x8u5">
                        <td className="py-2 text-[13px] text-ink" data-id="element-3243" data-mp="rjzo9kru8evv">{ACCOUNT_LABEL[entry.account]}</td>
                        <td className="cz-num py-2 text-right text-[13px] text-ink-muted" data-id="element-3244" data-mp="whho8lnvnjrq">
                          {entry.direction === 'debit' ? naira(entry.amountNaira) : '—'}
                        </td>
                        <td className="cz-num py-2 text-right text-[13px] text-ink-muted" data-id="element-3245" data-mp="2hz8ni195hz6u">
                          {entry.direction === 'credit' ? naira(entry.amountNaira) : '—'}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                <dl className="mt-3 grid gap-x-6 gap-y-2 border-t border-line pt-3 sm:grid-cols-2" data-id="element-3246" data-mp="249ydurvwvp7g">
                  <div className="flex justify-between gap-3" data-id="element-3247" data-mp="1zb3o15zj10du">
                    <dt className="text-[12px] text-ink-muted" data-id="element-3248" data-mp="2d0yt3sv932ma">Provider</dt>
                    <dd className="text-[12px] font-medium text-ink" data-id="element-3249" data-mp="2d31s32eds635">
                      {transaction.provider ?? 'Internal — no provider'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-3250" data-mp="1kitdm0ot3e5s">
                    <dt className="text-[12px] text-ink-muted" data-id="element-3251" data-mp="1gijaqwwb93v5">Provider reference</dt>
                    <dd className="cz-num text-[12px] font-medium text-ink" data-id="element-3252" data-mp="16f4565s0w580">
                      {transaction.providerReference ?? '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-3253" data-mp="whq2d8z0vey6">
                    <dt className="text-[12px] text-ink-muted" data-id="element-3254" data-mp="wf3o6qhmm16z">Balanced</dt>
                    <dd className="text-[12px] font-medium text-ink" data-id="element-3255" data-mp="11gsvpw5n3wqb">
                      {isBalanced(transaction) ? 'Yes' : 'No — flagged'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-3256" data-mp="1u3nd2k75ufzk">
                    <dt className="text-[12px] text-ink-muted" data-id="element-3257" data-mp="1f87h29n0sa0j">Counterparty</dt>
                    <dd className="text-[12px] font-medium text-ink" data-id="element-3258" data-mp="1k6sha929utoz">{transaction.counterparty}</dd>
                  </div>
                </dl>
                {transaction.correctsReference && <p className="mt-3 flex items-start gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-[12px] leading-5 text-ink-muted" data-id="element-3259" data-mp="8rlzx4xkll8r">
                    <LinkIcon className="mt-0.5 h-3 w-3 shrink-0" data-id="element-3260" data-mp="2bczhska9g4q4" />
                    Corrects {transaction.correctsReference}. The original record is never edited —
                    corrections are separate immutable transactions.
                  </p>}
              </div>}
          </li>;
    })}
    </ul>;
}